import {
  EAssetSource,
  EAssetType,
  getRefId,
  type FindexInvestment,
  type FullInvestment,
  type IValue
} from 'findef'
import { type FindexAsset } from '../../types/models/asset'
import { getAssetType } from '../asset'
import { computeRoi } from '../roi'
import { EShareholderType } from 'findef'
import type { FindexUser } from '@/types/models/user'
import { getUserFullName } from '../user'
import { createInvestmentMap, type FindexInvestmentMap } from '../asset/map'
import { notNullish } from '@vueuse/core'

export const getInvestmentDate = (investment: FindexInvestment): Date => {
  return investment.invested?.time || investment.asset?.createdAt || new Date()
}

export const getInvestmentType = (investment: FindexInvestment): EAssetType => {
  if (typeof investment.asset === 'undefined') return EAssetType.ALTERNATIVE
  return getAssetType(investment.asset as unknown as FindexAsset)
}

export const isInvestmentListed = (investment: FindexInvestment): boolean | undefined => {
  return investment?.asset?.listed
}

export const getInvestmentInitialValue = (
  investment: FindexInvestment | FullInvestment
): number => {
  return investment?.invested?.value || 0
}

export const getInvestmentInitialCurrency = (
  investment: FindexInvestment | FullInvestment
): string | undefined => {
  return investment?.invested?.type
}

export const getInvestmentQuantity = (investment: FindexInvestment | FullInvestment): number => {
  return investment.quantity
}

export const getInvestmentROIPercentage = (inv: FindexInvestment) => {
  const currentValue = getInvestmentCurrentValue(inv)
  const initialValue = getInvestmentInitialValue(inv)
  return computeRoi(initialValue, currentValue)
}

export const getInvestmentCurrentValue = (
  investment: FindexInvestment | FullInvestment
): number => {
  return (
    investment.currentValue?.value ||
    investment.asset?.ledger?.sharePrice?.value * investment.quantity ||
    0
  )
}

export const getInvestmentInvested = (investment: FindexInvestment | FullInvestment): IValue => {
  return investment.invested
}

export const getInvestmentCurrentValueCurrency = (
  investment: FindexInvestment | FullInvestment
): string | undefined => {
  if (
    (investment.asset.commodityQuote ||
      investment.asset.cryptoQuote ||
      investment.asset.commodityQuote) &&
    investment.asset?.ledger?.sharePrice?.type
  )
    return investment.asset?.ledger?.sharePrice?.type
  if (investment.currentValue?.value && investment.currentValue?.type)
    return investment.currentValue.type
  return investment.asset?.ledger?.sharePrice?.type
}

export const getInvestmentValue = (investment: FindexInvestment | FullInvestment): number => {
  return getInvestmentCurrentValue(investment)
}

export const getInvestmentTotalInvested = (investment: FindexInvestment): number => {
  return investment?.invested?.value || 0
}

export const getInvestmentId = (investment: FindexInvestment): string => {
  return getRefId(investment)
}

export const prettyShareholderType = (type: EShareholderType | undefined) => {
  switch (type) {
    case EShareholderType.RETAIL_INVESTOR:
      return 'Retail Investor'
    case EShareholderType.ANGEL_INVESTOR:
      return 'Angel Investor'
    case EShareholderType.FOUNDER:
      return 'Founder'
    case EShareholderType.EMPLOYEE:
      return 'Employee'
    case EShareholderType.VC:
      return 'VC'
    case EShareholderType.OTHER:
      return 'Other'
    case EShareholderType.INVESTMENT_COMPANY:
      return 'Investment Company'
    default:
      return 'Retail Investor'
  }
}

export const getOwnedBy = (shareholder: FindexUser, investment?: FindexInvestment) => {
  const name =
    ((investment?.ownedBy?.name || '').length > 1
      ? investment?.ownedBy?.name
      : getUserFullName(shareholder)) || 'No Name'

  if (investment?.ownedBy && investment?.ownedBy?.name) {
    return {
      name: name,
      organizationNbr: investment.ownedBy?.organizationNbr,
      ownedByCompany: true
    }
  }
  if (shareholder?.firstname && shareholder?.lastname) {
    return { name: name }
  }
  return { name: name }
}
// createInvestmentMap(investments.value)

export const investmentContains = (
  investment: FindexInvestment | FullInvestment,
  other: FindexInvestment | FullInvestment,
  investments: Array<FindexInvestment | FullInvestment>,
  invMap?: FindexInvestmentMap
): boolean => {
  const map = invMap || createInvestmentMap(investments)
  const invId = getRefId(investment)
  const otherId = getRefId(other)
  if (otherId === invId) return true

  const childIds = investment.childrenIds || []
  const childInvestments = childIds.map((it) => map[getRefId(it)]).filter(notNullish)

  for (const nextChild of childInvestments) {
    if (getRefId(nextChild) === getRefId(other)) return true

    if (investmentContains(nextChild, other, investments, map)) {
      return true
    }
  }

  return false
}

export const investmentIsAbove = (
  investment: FindexInvestment | FullInvestment,
  other: FindexInvestment | FullInvestment,
  investments: Array<FindexInvestment | FullInvestment>,
  invMap?: FindexInvestmentMap
) => {
  const map = invMap || createInvestmentMap(investments)
  const invId = getRefId(investment)

  let nextParent = other.parentId ? map[getRefId(other.parentId)] : null

  // check all parents
  while (nextParent) {
    if (getRefId(nextParent) === invId) return true
    nextParent = nextParent.parentId ? map[getRefId(nextParent.parentId)] : null
  }

  return false
}

export const canBeParentOf = (
  potentialParent: FindexInvestment | FullInvestment,
  child: FindexInvestment | FullInvestment,
  investments: Array<FindexInvestment | FullInvestment>,
  invMap?: FindexInvestmentMap
) => {
  const map = invMap || createInvestmentMap(investments)
  const parentId = getRefId(potentialParent)
  const childId = getRefId(child)
  if (parentId === childId) return false

  // If it already is a parent of the child, we assume it's OK.
  if (parentId === child.parentId) return true

  // If the child is "above" the parent, then the potential parent can't be a parent.
  if (investmentIsAbove(child, potentialParent, investments, map)) return false
  // If the parent exists within the tree of the child, then false.
  if (investmentContains(child, potentialParent, investments, map)) return false

  return true
}

export const getPotentialParents = (
  child: FindexInvestment | FullInvestment | null | undefined,
  investments: Array<FindexInvestment | FullInvestment>,
  invMap?: FindexInvestmentMap
) => {
  if (!child) {
    return investments.filter(
      (it) =>
        it.asset.type === EAssetType.EQUITY &&
        !it.asset.listed &&
        it.asset.source !== EAssetSource.IR
    )
  }

  const map = invMap || createInvestmentMap(investments)

  return investments.filter(
    (it) =>
      canBeParentOf(it, child, investments, map) &&
      it.asset.type === EAssetType.EQUITY &&
      !it.asset.listed &&
      it.asset.source !== EAssetSource.IR
  )
}

export const isDescendant = (
  parentId: string,
  childId: string,
  investments: FindexInvestment[]
): boolean => {
  const parent = investments.find((a) => a._id === parentId)
  if (!parent || !parent.childrenIds) return false

  if (parent.childrenIds.includes(childId)) {
    return true
  }

  return parent.childrenIds.some((id) => isDescendant(id as string, childId, investments))
}
